import React, { useEffect, useState, useCallback } from "react";import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Loader2, LogOut, RefreshCw, Mail, Users, MessageSquare, PackageCheck,
  KeyRound, Home, TrendingUp, Send, Settings as SettingsIcon, CreditCard,
  Save, Sparkles,
} from "lucide-react";
import { API, useAuth } from "@/App";

const brl = (n) => (n ?? 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL", minimumFractionDigits: 0 });
const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString("pt-BR"); } catch { return iso; }
};
function fmtErr(d) {
  if (!d) return "Erro desconhecido";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(d?.msg || d);
}

export default function AdminPage() {
  const { user, checking, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [newsletter, setNewsletter] = useState([]);
  const [users, setUsers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);

  const notAdmin = !checking && (!user || user.role !== "admin");

  useEffect(() => {
    if (notAdmin) navigate("/");
  }, [notAdmin, navigate]);

  useEffect(() => {
    if (user?.must_change_password) setPwOpen(true);
  }, [user]);

  const loadAll = useCallback(async () => {
    if (!user || user.role !== "admin") return;
    setLoading(true);
    try {
      const [ov, rs, ct, nl, us, pm, st] = await Promise.all([
        axios.get(`${API}/admin/overview`),
        axios.get(`${API}/admin/reservations`),
        axios.get(`${API}/admin/contacts`),
        axios.get(`${API}/admin/newsletter`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/payments`),
        axios.get(`${API}/admin/settings`),
      ]);
      setOverview(ov.data);
      setReservations(rs.data);
      setContacts(ct.data);
      setNewsletter(nl.data);
      setUsers(us.data);
      setPayments(pm.data);
      setSettings(st.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { loadAll(); }, [loadAll]);

  if (checking || notAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-[color:var(--pm-neon)]" />
      </div>
    );
  }

  const tabs = [
    { id: "overview", label: "Visão geral", icon: TrendingUp },
    { id: "reservations", label: "Reservas", icon: PackageCheck },
    { id: "payments", label: "Pagamentos", icon: CreditCard },
    { id: "contacts", label: "Contatos", icon: MessageSquare },
    { id: "newsletter", label: "Newsletter", icon: Send },
    { id: "users", label: "Clientes", icon: Users },
    { id: "settings", label: "Configurações", icon: SettingsIcon },
  ];

  return (
    <div className="min-h-screen bg-[var(--pm-bg)] text-white" data-testid="admin-page">
      <header className="pm-glass sticky top-0 z-40 border-b border-white/5">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 h-16">
          <div className="flex items-center gap-3">
            <div className="pm-label">// Painel Admin</div>
            <span className="text-xs text-white/40 font-mono">{user.email}</span>
          </div>
          <div className="flex items-center gap-2">
            <button className="pm-btn-ghost text-xs" onClick={() => navigate("/")} data-testid="admin-goto-site">
              <Home size={14} className="inline -mt-1 mr-1" /> Site
            </button>
            <button className="pm-btn-ghost text-xs" onClick={loadAll} data-testid="admin-refresh">
              <RefreshCw size={14} className={`inline -mt-1 mr-1 ${loading ? "animate-spin" : ""}`} /> Atualizar
            </button>
            <button className="pm-btn-ghost text-xs" onClick={() => setPwOpen(true)} data-testid="admin-change-password-open">
              <KeyRound size={14} className="inline -mt-1 mr-1" /> Trocar senha
            </button>
            <button className="pm-btn-ghost text-xs" onClick={async () => { await logout(); navigate("/"); }} data-testid="admin-logout">
              <LogOut size={14} className="inline -mt-1 mr-1" /> Sair
            </button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 pb-3 flex gap-1 overflow-x-auto">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`text-xs uppercase tracking-widest px-4 py-2 rounded-full border transition-colors ${
                tab === t.id
                  ? "bg-[color:var(--pm-neon)] text-black border-transparent"
                  : "border-white/10 text-white/70 hover:border-[color:var(--pm-neon)] hover:text-[color:var(--pm-neon)]"
              }`}
            >
              <t.icon size={13} className="inline -mt-0.5 mr-1" />
              {t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {tab === "overview" && <Overview data={overview} />}
        {tab === "reservations" && <ReservationsTable items={reservations} />}
        {tab === "payments" && <PaymentsTable items={payments} />}
        {tab === "contacts" && <ContactsTable items={contacts} />}
        {tab === "newsletter" && <NewsletterTable items={newsletter} />}
        {tab === "users" && <UsersTable items={users} />}
        {tab === "settings" && <SettingsPanel data={settings} onSaved={loadAll} />}
      </main>

      {pwOpen && (
        <ChangePasswordDialog
          forced={user.must_change_password}
          onClose={() => setPwOpen(false)}
        />
      )}
    </div>
  );
}

/* --- Overview ---------------------------------------------------------- */
function Overview({ data }) {
  if (!data) return <div className="text-white/50">Carregando...</div>;
  const cards = [
    { label: "Reservas ativas", value: `${data.sold} / ${data.total_units}`, accent: true },
    { label: "Sinais pagos", value: data.paid ?? 0 },
    { label: "Total arrecadado", value: brl(data.deposit_total ?? 0) },
    { label: "Unidades restantes", value: data.remaining },
    { label: "Clientes", value: data.users },
    { label: "Contatos", value: data.contacts },
    { label: "Newsletter", value: data.newsletter },
  ];
  return (
    <div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} className={`pm-card p-5 ${c.accent ? "pm-glow-ring" : ""}`}>
            <div className="pm-label text-[10px]">{c.label}</div>
            <div className={`font-mono text-2xl mt-2 ${c.accent ? "pm-neon-text" : "text-white"}`}>{c.value}</div>
          </div>
        ))}
      </div>

      <div className="pm-card p-6 mt-6">
        <div className="pm-label mb-4">// Reservas por modelo</div>
        <div className="grid sm:grid-cols-4 gap-3">
          {["essential", "advanced", "premium", "platinum"].map((slug) => (
            <div key={slug} className="border border-white/10 rounded-lg p-4">
              <div className="text-xs uppercase tracking-widest text-white/60">{slug}</div>
              <div className="font-mono text-2xl mt-2">{data.per_model?.[slug] || 0}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* --- Tables ------------------------------------------------------------ */
function TableShell({ title, empty, columns, children }) {
  return (
    <div className="pm-card overflow-hidden">
      <div className="px-5 py-4 border-b border-white/5">
        <div className="pm-label">// {title}</div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-widest text-white/40">
              {columns.map((c) => <th key={c} className="px-5 py-3 font-medium">{c}</th>)}
            </tr>
          </thead>
          <tbody>
            {React.Children.count(children) === 0 ? (
              <tr><td colSpan={columns.length} className="px-5 py-10 text-center text-white/40">{empty}</td></tr>
            ) : children}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ReservationsTable({ items }) {
  return (
    <TableShell title="Reservas" empty="Nenhuma reserva ainda" columns={["Data", "Cliente", "Modelo", "Valor", "WhatsApp", "Status"]}>
      {items.map((r) => (
        <tr key={r.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`row-reservation-${r.id}`}>
          <td className="px-5 py-3 font-mono text-xs text-white/60">{fmtDate(r.created_at)}</td>
          <td className="px-5 py-3">
            <div>{r.user_name}</div>
            <div className="text-xs text-white/50">{r.user_email}</div>
          </td>
          <td className="px-5 py-3">{r.model_name}</td>
          <td className="px-5 py-3 font-mono pm-neon-text">{brl(r.model_price)}</td>
          <td className="px-5 py-3 font-mono text-xs">
            <a
              href={`https://wa.me/${(r.user_phone || "").replace(/\D/g, "")}`}
              target="_blank" rel="noreferrer"
              className="text-[color:var(--pm-neon)] hover:underline"
            >
              {r.user_phone}
            </a>
          </td>
          <td className="px-5 py-3">
            <span className="text-xs px-2 py-1 rounded-full bg-white/5 border border-white/10">{r.status}</span>
          </td>
        </tr>
      ))}
    </TableShell>
  );
}

function ContactsTable({ items }) {
  return (
    <TableShell title="Mensagens de contato" empty="Nenhuma mensagem" columns={["Data", "Nome", "Contato", "Mensagem"]}>
      {items.map((c) => (
        <tr key={c.id} className="border-t border-white/5 align-top" data-testid={`row-contact-${c.id}`}>
          <td className="px-5 py-3 font-mono text-xs text-white/60 whitespace-nowrap">{fmtDate(c.created_at)}</td>
          <td className="px-5 py-3">{c.name}</td>
          <td className="px-5 py-3 text-xs">
            <div>{c.email}</div>
            {c.phone && <div className="text-white/50">{c.phone}</div>}
          </td>
          <td className="px-5 py-3 text-white/80 max-w-xl">{c.message}</td>
        </tr>
      ))}
    </TableShell>
  );
}

function NewsletterTable({ items }) {
  return (
    <TableShell title="Inscrições na newsletter" empty="Ninguém inscrito ainda" columns={["Data", "E-mail"]}>
      {items.map((n) => (
        <tr key={n.id} className="border-t border-white/5" data-testid={`row-newsletter-${n.id}`}>
          <td className="px-5 py-3 font-mono text-xs text-white/60">{fmtDate(n.created_at)}</td>
          <td className="px-5 py-3"><Mail size={14} className="inline -mt-1 mr-2 text-white/40" />{n.email}</td>
        </tr>
      ))}
    </TableShell>
  );
}

function UsersTable({ items }) {
  return (
    <TableShell title="Clientes cadastrados" empty="Nenhum cliente" columns={["Data", "Nome", "E-mail", "WhatsApp", "Perfil"]}>
      {items.map((u) => (
        <tr key={u.id} className="border-t border-white/5" data-testid={`row-user-${u.id}`}>
          <td className="px-5 py-3 font-mono text-xs text-white/60">{fmtDate(u.created_at)}</td>
          <td className="px-5 py-3">{u.name}</td>
          <td className="px-5 py-3">{u.email}</td>
          <td className="px-5 py-3">{u.phone}</td>
          <td className="px-5 py-3 text-xs uppercase tracking-widest text-white/60">{u.user_type}</td>
        </tr>
      ))}
    </TableShell>
  );
}

function PaymentsTable({ items }) {
  return (
    <TableShell
      title="Pagamentos (Stripe)"
      empty="Nenhum pagamento ainda"
      columns={["Data", "Cliente", "Valor", "Status", "Sessão"]}
    >
      {items.map((p) => {
        const st = p.payment_status || p.status;
        const color =
          st === "paid" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
          : st === "expired" || st === "unpaid" ? "bg-red-500/15 text-red-300 border-red-500/30"
          : "bg-yellow-500/10 text-yellow-300 border-yellow-500/30";
        return (
          <tr key={p.id} className="border-t border-white/5" data-testid={`row-payment-${p.id}`}>
            <td className="px-5 py-3 font-mono text-xs text-white/60">{fmtDate(p.created_at)}</td>
            <td className="px-5 py-3 text-sm">{p.user_email}</td>
            <td className="px-5 py-3 font-mono pm-neon-text">{brl(p.amount)}</td>
            <td className="px-5 py-3">
              <span className={`text-xs px-2 py-1 rounded-full border ${color}`}>{st}</span>
            </td>
            <td className="px-5 py-3 font-mono text-[11px] text-white/40">{p.session_id?.slice(0, 22)}...</td>
          </tr>
        );
      })}
    </TableShell>
  );
}

function SettingsPanel({ data, onSaved }) {
  const [launch, setLaunch] = useState("");
  const [promoEnabled, setPromoEnabled] = useState(false);
  const [promoText, setPromoText] = useState("");
  const [promoLink, setPromoLink] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactEmailSales, setContactEmailSales] = useState("");
  const [contactWhatsapp, setContactWhatsapp] = useState("");
  const [contactCity, setContactCity] = useState("");
  const [instagram, setInstagram] = useState("");
  const [facebook, setFacebook] = useState("");
  const [mpLink, setMpLink] = useState("");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    if (!data) return;
    const d = data.launch_deadline ? new Date(data.launch_deadline) : null;
    setLaunch(d ? d.toISOString().slice(0, 10) : "");
    setPromoEnabled(!!data.promo_enabled);
    setPromoText(data.promo_text || "");
    setPromoLink(data.promo_link || "");
    setContactEmail(data.contact_email || "");
    setContactEmailSales(data.contact_email_sales || "");
    setContactWhatsapp(data.contact_whatsapp || "");
    setContactCity(data.contact_city || "");
    setInstagram(data.instagram || "");
    setFacebook(data.facebook || "");
    setMpLink(data.mercadopago_link || "");
  }, [data]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setMsg("");
    try {
      await axios.put(`${API}/admin/settings`, {
        launch_deadline: launch,
        promo_enabled: promoEnabled,
        promo_text: promoText,
        promo_link: promoLink,
        contact_email: contactEmail,
        contact_email_sales: contactEmailSales,
        contact_whatsapp: contactWhatsapp.replace(/\D/g, ""),
        contact_city: contactCity,
        instagram,
        facebook,
        mercadopago_link: mpLink,
      });
      setMsg("Configurações salvas com sucesso!");
      onSaved?.();
    } catch (err) {
      setMsg(fmtErr(err.response?.data?.detail) || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <div className="text-white/50">Carregando...</div>;

  return (
    <form onSubmit={save} className="grid lg:grid-cols-2 gap-6 max-w-5xl">
      <div className="pm-card p-6">
        <div className="pm-label mb-4">// Contador regressivo</div>
        <p className="text-white/60 text-sm mb-4">
          Data-fim exibida no site como contador regressivo do pré-lançamento.
        </p>
        <label className="block text-xs text-white/60 mb-2">Data de encerramento (dd/mm/aaaa)</label>
        <input
          type="date"
          className="pm-input"
          value={launch}
          onChange={(e) => setLaunch(e.target.value)}
          data-testid="settings-launch-date"
          required
        />
        <p className="text-[11px] text-white/40 mt-3 font-mono">
          Atual: {data.launch_deadline ? new Date(data.launch_deadline).toLocaleString("pt-BR") : "—"}
        </p>
      </div>

      <div className="pm-card p-6">
        <div className="pm-label mb-4">// Banner de promoções</div>
        <p className="text-white/60 text-sm mb-4">
          Barra no topo do site para divulgar promoções de insumos ou avisos.
        </p>
        <label className="flex items-center gap-3 text-sm mb-4 cursor-pointer">
          <input
            type="checkbox"
            className="h-4 w-4 accent-[color:var(--pm-neon)]"
            checked={promoEnabled}
            onChange={(e) => setPromoEnabled(e.target.checked)}
            data-testid="settings-promo-enabled"
          />
          <span>Exibir banner no site</span>
        </label>
        <label className="block text-xs text-white/60 mb-2">Texto do banner</label>
        <input
          className="pm-input"
          placeholder="Ex: Insumos com 15% OFF até domingo!"
          value={promoText}
          onChange={(e) => setPromoText(e.target.value)}
          maxLength={140}
          data-testid="settings-promo-text"
        />
        <label className="block text-xs text-white/60 mb-2 mt-3">Link (opcional)</label>
        <input
          className="pm-input"
          placeholder="https://..."
          value={promoLink}
          onChange={(e) => setPromoLink(e.target.value)}
          data-testid="settings-promo-link"
        />
        {promoEnabled && promoText && (
          <div className="mt-4 rounded-lg overflow-hidden text-xs">
            <div className="pm-label mb-2 text-[10px]">Prévia</div>
            <div className="bg-gradient-to-r from-[#00f0ff] via-[#33f5ff] to-[#00f0ff] text-black font-semibold py-2 px-4 flex items-center gap-2 justify-center">
              <Sparkles size={14} /> {promoText}
            </div>
          </div>
        )}
      </div>

      <div className="pm-card p-6 lg:col-span-2">
        <div className="pm-label mb-4">// Contato exibido no site</div>
        <p className="text-white/60 text-sm mb-5">
          Esses dados aparecem no rodapé, no botão WhatsApp e nos links de contato do site.
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-white/60 mb-2">WhatsApp (com DDI e DDD, só números)</label>
            <input
              className="pm-input"
              placeholder="5547999711358"
              value={contactWhatsapp}
              onChange={(e) => setContactWhatsapp(e.target.value)}
              data-testid="settings-whatsapp"
            />
            <p className="text-[11px] text-white/40 mt-1 font-mono">Ex: 5547999999999</p>
          </div>
          <div>
            <label className="block text-xs text-white/60 mb-2">Cidade / Endereço curto</label>
            <input
              className="pm-input"
              placeholder="Joinville · SC · Brasil"
              value={contactCity}
              onChange={(e) => setContactCity(e.target.value)}
              data-testid="settings-city"
            />
          </div>
          <div>
            <label className="block text-xs text-white/60 mb-2">E-mail principal (contato)</label>
            <input
              type="email"
              className="pm-input"
              placeholder="contato@petmachine.ind.br"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              data-testid="settings-email"
            />
          </div>
          <div>
            <label className="block text-xs text-white/60 mb-2">E-mail de vendas (opcional)</label>
            <input
              type="email"
              className="pm-input"
              placeholder="vendas@petmachine.ind.br"
              value={contactEmailSales}
              onChange={(e) => setContactEmailSales(e.target.value)}
              data-testid="settings-email-sales"
            />
          </div>
          <div>
            <label className="block text-xs text-white/60 mb-2">Instagram (@handle) — opcional</label>
            <input
              className="pm-input"
              placeholder="@petmachine.ind"
              value={instagram}
              onChange={(e) => setInstagram(e.target.value)}
              data-testid="settings-instagram"
            />
          </div>
          <div>
            <label className="block text-xs text-white/60 mb-2">Facebook (/pagina) — opcional</label>
            <input
              className="pm-input"
              placeholder="/petmachine"
              value={facebook}
              onChange={(e) => setFacebook(e.target.value)}
              data-testid="settings-facebook"
            />
          </div>
        </div>
      </div>

      <div className="pm-card p-6 lg:col-span-2">
        <div className="pm-label mb-4">// Pagamento — Mercado Pago</div>
        <p className="text-white/60 text-sm mb-4">
          Cole aqui seu link público do Mercado Pago. Ele aparece como opção alternativa (PIX/boleto) no momento da reserva.
        </p>
        <label className="block text-xs text-white/60 mb-2">Link Mercado Pago</label>
        <input
          className="pm-input"
          placeholder="https://link.mercadopago.com.br/petmachineind"
          value={mpLink}
          onChange={(e) => setMpLink(e.target.value)}
          data-testid="settings-mp-link"
        />
        <p className="text-[11px] text-white/40 mt-2">
          Deixe em branco para não exibir a opção. O Stripe (cartão) continua funcionando normalmente.
        </p>
      </div>

      <div className="lg:col-span-2 flex items-center justify-between gap-3 pm-card p-5">
        <div className="text-sm text-white/70">
          {msg && <span data-testid="settings-msg">{msg}</span>}
        </div>
        <button className="pm-btn-primary text-xs flex items-center gap-2" disabled={saving} data-testid="settings-save-button">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Salvar configurações
        </button>
      </div>
    </form>
  );
}

/* --- Change password dialog ------------------------------------------- */
function ChangePasswordDialog({ forced, onClose }) {
  const { refresh } = useAuth();
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [ok, setOk] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setOk(false);
    if (next.length < 8) { setError("A nova senha precisa ter no mínimo 8 caracteres."); return; }
    if (next !== confirm) { setError("As senhas não coincidem."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: cur, new_password: next });
      setOk(true);
      await refresh();
      setTimeout(() => onClose?.(), 900);
    } catch (err) {
      setError(fmtErr(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="change-password-dialog">
      <div className="pm-card pm-glow-ring w-full max-w-md p-8 relative">
        <div className="pm-label mb-2">// Trocar senha {forced && "(obrigatório)"}</div>
        <h3 className="font-display text-2xl font-black">Defina sua senha</h3>
        {forced && (
          <p className="text-yellow-300/90 text-sm mt-2 bg-yellow-500/10 border border-yellow-500/20 rounded-lg px-3 py-2">
            Primeiro acesso: você precisa criar uma nova senha antes de continuar.
          </p>
        )}
        <form onSubmit={submit} className="mt-5 space-y-3">
          <input type="password" className="pm-input" placeholder="Senha atual"
                 value={cur} onChange={(e) => setCur(e.target.value)} required
                 data-testid="cp-current" />
          <input type="password" className="pm-input" placeholder="Nova senha (mínimo 8 caracteres)"
                 value={next} onChange={(e) => setNext(e.target.value)} required minLength={8}
                 data-testid="cp-new" />
          <input type="password" className="pm-input" placeholder="Confirmar nova senha"
                 value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={8}
                 data-testid="cp-confirm" />
          {error && <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2" data-testid="cp-error">{error}</div>}
          {ok && <div className="text-emerald-400 text-sm bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-3 py-2" data-testid="cp-success">Senha atualizada com sucesso!</div>}
          <div className="flex gap-2 pt-2">
            {!forced && (
              <button type="button" className="pm-btn-ghost text-xs flex-1" onClick={onClose} data-testid="cp-cancel">Cancelar</button>
            )}
            <button className="pm-btn-primary text-xs flex-1" disabled={loading} data-testid="cp-submit">
              {loading ? <Loader2 size={14} className="animate-spin inline" /> : "Salvar nova senha"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
